import { z } from 'zod';

// ============================================================
// Mileage Entry Schemas (Story 2.2 + 3.2, 3.4)
// ============================================================

const isNotFuture = (date: Date) => date <= new Date();

const locationTypeSchema = z.enum(['HOME', 'OFFICE', 'OTHER']).default('OTHER');

export const createMileageSchema = z.object({
  date: z
    .string()
    .datetime({ offset: true })
    .or(z.string().date())
    .transform((val) => new Date(val))
    .refine(isNotFuture, { message: 'Date cannot be in the future' }),
  origin: z.string().min(1, 'Origin is required').max(500),
  destination: z.string().min(1, 'Destination is required').max(500),
  purpose: z.string().max(500).optional(),
  vehicleType: z.string().max(100).optional(),
  isRoundTrip: z.boolean().default(false),
  originType: locationTypeSchema,
  destType: locationTypeSchema,
  // Round trip: where the trip ends. The server defaults it to origin, so
  // offline drafts queued by older clients still sync.
  finalDestination: z.string().min(1).max(500).optional(),
  finalDestType: z.enum(['HOME', 'OFFICE', 'OTHER']).optional(),
  // km of the route the user picked; the server checks it against its own route list
  routeDistanceKm: z.number().positive().optional(),
  returnRouteDistanceKm: z.number().positive().optional(),
  // Whole-trip total (both legs of a round trip)
  distanceOverride: z.number().positive().optional(),
  overrideReason: z.string().max(500).optional(),
  parkingEntryId: z.string().uuid().nullable().optional(),
  tollEntryId: z.string().uuid().nullable().optional(),
  userLocationId: z.string().uuid().nullable().optional(),
  clientLocalId: z.string().uuid().optional(),
}).superRefine((data, ctx) => {
  // Story 3.4: overrideReason is required when distanceOverride is set
  if (data.distanceOverride != null && !data.overrideReason?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Override reason is required when distance override is provided',
      path: ['overrideReason'],
    });
  }
});

export type CreateMileageInput = z.input<typeof createMileageSchema>;

export const updateMileageSchema = z.object({
  date: z
    .string()
    .datetime({ offset: true })
    .or(z.string().date())
    .transform((val) => new Date(val))
    .refine(isNotFuture, { message: 'Date cannot be in the future' })
    .optional(),
  origin: z.string().min(1, 'Origin is required').max(500).optional(),
  destination: z.string().min(1, 'Destination is required').max(500).optional(),
  purpose: z.string().max(500).optional(),
  vehicleType: z.string().max(100).optional(),
  isRoundTrip: z.boolean().optional(),
  originType: z.enum(['HOME', 'OFFICE', 'OTHER']).optional(),
  destType: z.enum(['HOME', 'OFFICE', 'OTHER']).optional(),
  finalDestination: z.string().min(1).max(500).nullable().optional(),
  finalDestType: z.enum(['HOME', 'OFFICE', 'OTHER']).nullable().optional(),
  routeDistanceKm: z.number().positive().nullable().optional(),
  returnRouteDistanceKm: z.number().positive().nullable().optional(),
  distanceOverride: z.number().positive().nullable().optional(),
  overrideReason: z.string().max(500).optional(),
  parkingEntryId: z.string().uuid().nullable().optional(),
  tollEntryId: z.string().uuid().nullable().optional(),
  userLocationId: z.string().uuid().nullable().optional(),
}).superRefine((data, ctx) => {
  if (data.distanceOverride != null && !data.overrideReason?.trim()) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Override reason is required when distance override is provided',
      path: ['overrideReason'],
    });
  }
});

export type UpdateMileageInput = z.input<typeof updateMileageSchema>;

export const mileageResponseSchema = z.object({
  id: z.string(),
  claimId: z.string(),
  date: z.date().or(z.string()),
  origin: z.string(),
  destination: z.string(),
  purpose: z.string().nullable(),
  vehicleType: z.string().nullable(),
  originType: z.string(),
  destType: z.string(),
  distance: z.union([z.number(), z.string()]),
  distanceOverride: z.union([z.number(), z.string()]).nullable(),
  overrideReason: z.string().nullable(),
  homeOfficeDeduction: z.union([z.number(), z.string()]),
  claimableDistance: z.union([z.number(), z.string()]),
  rateUsed: z.union([z.number(), z.string()]).nullable(),
  amount: z.union([z.number(), z.string()]),
  isRoundTrip: z.boolean(),
  finalDestination: z.string().nullable().optional(),
  finalDestType: z.string().nullable().optional(),
  returnDistance: z.union([z.number(), z.string()]).nullable().optional(),
  routeSummary: z.string().nullable().optional(),
  returnRouteSummary: z.string().nullable().optional(),
  parkingEntryId: z.string().nullable().optional(),
  tollEntryId: z.string().nullable().optional(),
  userLocationId: z.string().nullable().optional(),
  // Issue #12 (A6) Verification fields
  adminVerified: z.boolean().default(false),
  adminVerifiedById: z.string().nullable().optional(),
  adminVerifiedAt: z.date().or(z.string()).nullable().optional(),
  financeVerified: z.boolean().default(false),
  financeVerifiedById: z.string().nullable().optional(),
  financeVerifiedAt: z.date().or(z.string()).nullable().optional(),
  hrVerified: z.boolean().default(false),
  hrVerifiedById: z.string().nullable().optional(),
  hrVerifiedAt: z.date().or(z.string()).nullable().optional(),
  flagReason: z.string().nullable().optional(),
  flaggedById: z.string().nullable().optional(),
  flaggedAt: z.date().or(z.string()).nullable().optional(),
  createdAt: z.date().or(z.string()),
  updatedAt: z.date().or(z.string()),
});

export type MileageResponse = z.infer<typeof mileageResponseSchema>;

